import { DOCUMENT } from '@angular/common';
import { inject, Injectable, RendererFactory2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LocaleService } from './locale.service';

/**
 * Maintains the per-page SEO link tags for the language URL scheme:
 *  - `<link rel="canonical">` pointing at the current page in the current language
 *  - `<link rel="alternate" hreflang="…">` for every supported language
 *  - `<link rel="alternate" hreflang="x-default">` for the default language
 *
 * `<meta>` tags are handled by MetaService; this service owns `<link>` tags,
 * which Angular's Meta service does not manage. It runs both in SSR (so the
 * tags are in the server-rendered HTML) and on client-side navigation.
 */
@Injectable({ providedIn: 'root' })
export class SeoLinksService {
  private readonly router = inject(Router);
  private readonly localeService = inject(LocaleService);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(RendererFactory2).createRenderer(null, null);

  /** Call once at app start. Sets links for the current page and updates them on each navigation. */
  init(): void {
    this.updateLinks();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.updateLinks());
  }

  private updateLinks(): void {
    const origin = this.localeService.getOrigin();
    if (origin === '') {
      return;
    }

    // router.url is already the locale-stripped internal path (the logical path).
    const logicalPath = this.router.url;
    const currentLocale = this.localeService.getLocale();

    this.setLink('canonical', null, origin + this.localeService.addLocale(logicalPath, currentLocale));

    for (const locale of this.localeService.getSupportedLocales()) {
      this.setLink('alternate', locale, origin + this.localeService.addLocale(logicalPath, locale));
    }
    this.setLink(
      'alternate',
      'x-default',
      origin + this.localeService.addLocale(logicalPath, this.localeService.getDefaultLocale())
    );
  }

  /** Create the link tag if missing (matched by rel + optional hreflang), then set its href. */
  private setLink(rel: string, hreflang: string | null, href: string): void {
    const selector =
      hreflang === null ? `link[rel="${rel}"]` : `link[rel="${rel}"][hreflang="${hreflang}"]`;

    let link: HTMLLinkElement | null = this.document.head.querySelector(selector);
    if (!link) {
      link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', rel);
      if (hreflang !== null) {
        this.renderer.setAttribute(link, 'hreflang', hreflang);
      }
      this.renderer.appendChild(this.document.head, link);
    }
    this.renderer.setAttribute(link, 'href', href);
  }
}
